// Intent: "Szkoła finansów" — the Napper sleep-school pattern moved to family
// finance (mock: szko_a_finans_w_poprawiona): progress bar, day-by-day timeline
// cards, sequential unlock (reading a lesson unlocks the next). Content lives
// in src/content/school-lessons.ts (AI drafts, owner edits over time).

import { router } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { SCHOOL_LESSONS } from '@/content/school-lessons';
import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAppMetaStore } from '@/store/app-meta-store';
import { useHouseholdStore } from '@/store/household-store';
import { colors, radius, spacing } from '@/theme/tokens';

/** "domowych" once the household really has 2+ members, else "osobistych". */
export function schoolTitleOf(memberCount: number): string {
  return memberCount >= 2 ? 'Szkoła finansów domowych' : 'Szkoła finansów osobistych';
}

export default function SchoolScreen() {
  const { t } = useTranslation();
  const done = useAppMetaStore((s) => s.schoolDoneDays);
  const household = useHouseholdStore((s) => s.household);

  const memberCount = household?.members.length ?? 1;
  const pct = Math.round((100 * done.length) / SCHOOL_LESSONS.length);
  const maxUnlocked = (done.length ? Math.max(...done) : 0) + 1;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={schoolTitleOf(memberCount)} />
        <AppText variant="bodyMd" style={{ textAlign: 'center' }}>
          {t('school.subtitle')}
        </AppText>

        {/* Progress bar (mock: 21,9% ukończono) */}
        <View style={{ gap: spacing.xs }}>
          <View style={{ height: 8, borderRadius: radius.full, backgroundColor: colors.primaryTile, overflow: 'hidden' }}>
            <View style={{ height: 8, width: `${pct}%`, borderRadius: radius.full, backgroundColor: colors.primary }} />
          </View>
          <AppText variant="labelMd" style={{ textAlign: 'center' }}>
            {t('school.progress', { pct })}
          </AppText>
        </View>

        {/* Timeline of lessons */}
        {SCHOOL_LESSONS.map((lesson) => {
          const isDone = done.includes(lesson.day);
          const locked = lesson.day > maxUnlocked;
          return (
            <Pressable
              key={lesson.day}
              accessibilityRole="button"
              disabled={locked}
              onPress={() => router.push({ pathname: '/school/[day]', params: { day: String(lesson.day) } })}
              style={{ opacity: locked ? 0.55 : 1 }}
            >
              <Card style={{ flexDirection: 'row', gap: spacing.sm }}>
                <View style={{ alignItems: 'center', paddingTop: 2 }}>
                  <View
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 11,
                      backgroundColor: isDone ? colors.primary : 'transparent',
                      borderWidth: isDone ? 0 : 2,
                      borderColor: colors.outlineVariant,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {isDone && <Ionicons name="checkmark" size={14} color="#FFFFFF" />}
                  </View>
                </View>
                <View style={{ flex: 1, gap: 2 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
                    <View
                      style={{
                        backgroundColor: colors.primaryTile,
                        borderRadius: radius.full,
                        paddingHorizontal: spacing.sm,
                        paddingVertical: 2,
                      }}
                    >
                      <AppText variant="labelMd" color={colors.navy}>
                        {t('school.day', { day: lesson.day })}
                      </AppText>
                    </View>
                    {locked && <Ionicons name="lock-closed-outline" size={16} color={colors.outline} />}
                  </View>
                  <AppText variant="headlineSm">{lesson.title}</AppText>
                  <AppText variant="bodyMd" numberOfLines={3}>
                    {lesson.teaser}
                  </AppText>
                </View>
              </Card>
            </Pressable>
          );
        })}
      </ScrollView>
    </SafeAreaView>
  );
}
